/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { Image } from '@msdyn365-commerce/core';
import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import {
    IGroup,
    IGroupDelivery,
    IGroups
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-groups';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-order-information';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-sales-line';
import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/./order-confirmation';
import { ICoreContext } from '@msdyn365-commerce/core-internal';
//@ts-ignore
import { Primary } from '@churchofjesuschrist/eden-buttons';
import * as Msdyn365 from '@msdyn365-commerce/core';
import styled from 'styled-components';

import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { getStoreHoursAsync, getRetailChannelAsync, getWarehouseAsync } from '../../../actions/DataActionExtension.g';

interface IGroupsExtended extends IGroups {
    context: ICoreContext;
    resources?: any;
    bopis?: any;
}

interface IGroupExtended extends IGroup {
    context: ICoreContext;
    resources?: any;
    bopis?: any;
}

interface ISalesLineExtended extends ISalesLine {
    context: ICoreContext;
    quantity?: number;
    resources: any;
    showAddress?: boolean;
}

interface IOrderConfirmationExtended extends IOrderInformation {
    orderType: any;
    resources: any;
}

interface IOrderConfirmationPaymentExtended extends IPaymentMethods {
    paymentKey?: any;
    orderType?: any;
    resources?: any;
    salesOrder?: any;
    bankDeposit?: any;
}
interface IOrderConfirmationGroupExtended extends IGroupDelivery {
    salesLines?: any;
    bopis?: any;
}

export const OrderConfirmationOrderInfomation: React.FC<IOrderConfirmationExtended> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId,
    orderType,
    resources
}) => {
    const OrderWrapper = styled.div`
        display: flex !important;
        font-family: 'Ensign:Sans', Arial, 'noto sans', sans-serif;
        font-weight: normal;
        font-size: 16px;
        line-height: 21px;
        text-transform: none;
        display: block;
        margin-bottom: 10px;
        width: 100%;
    `;
    const ConfirmationId = styled.div`
        font-weight: 600;
        margin-left: 5px;
    `;
    //@ts-ignore
    const confirmationId = channelReferenceId.props.children[0].props.children[1].props.children;

    return (
        <Node {...orderInformationProps}>
            {createdDate}
            {orderType === 'Pickup Order' || orderType === 'Curbside pickup' ? (
                channelReferenceId
            ) : (
                <OrderWrapper>
                    <div>{resources.confirmationIdLabel}</div>
                    <ConfirmationId>{`${confirmationId}`}</ConfirmationId>
                </OrderWrapper>
            )}

            {receiptEmail}
        </Node>
    );
};

export const OrderConfirmationSalesLine: React.FC<ISalesLineExtended> = props => {
    const {
        data,
        // @ts-ignore
        count,
        context,
        quantity,
        showAddress,
        address
    } = props;
    const dimensions = data.product?.Dimensions?.map((dimension, index) => {
        return <div key={index}>{dimension.DimensionValue?.Value}</div>;
    });

    const fallbackImageUrl = getFallbackImageUrl(data.product?.ItemId, context.actionContext.requestContext.apiSettings);
    const SalesLineWrapper = styled.div`
        display: flex;
        width: 50%;
        margin-bottom: 15px;
        margin-top: 15px;

        @media (max-width: 660px) {
            width: 100%;
            justify-content: center;
        }
        @media (max-width: 840px) {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }
    `;

    const ItemDetails = styled.div`
        padding-left: 15px;
        @media (max-width: 840px) {
            padding-left: 0px;
            padding-bottom: 5px;
        }
    `;
    const ItemAddress = styled.div`
        border-left: 1px solid rgb(130, 121, 107);
        padding-left: 15px;
        @media (max-width: 840px) {
            padding-left: 0px;
            border-left: none;
            border-top: 1px solid rgb(130, 121, 107);
            padding-top: 5px;
        }
    `;

    //style settings for image can include width: w and height: h
    const imageGridSettings = { sm: { w: 100 } };

    return (
        <SalesLineWrapper>
            {/* <img src={data.product?.PrimaryImageUrl || fallbackImageUrl} style={{ width: '100px' }} /> */}
            <Image src={data.product?.PrimaryImageUrl ?? 'empty'} fallBackSrc={fallbackImageUrl} gridSettings={imageGridSettings} />
            <ItemDetails>
                <div style={{ fontWeight: 'bold' }}>{data.product?.Name}</div>
                {dimensions?.map((dimension, index) => dimension)}
                <div>
                    {props.resources.quantityLabel}
                    {quantity ? quantity : count}
                </div>
                <div style={{ fontWeight: 'bold' }}>
                    {data.salesStatus === 'processing' ? props.resources.processingLabel : data.salesStatus}
                </div>
            </ItemDetails>
            {showAddress && <ItemAddress>{address}</ItemAddress>}
        </SalesLineWrapper>
    );
};

export const OrderConfirmationGroupDelivery: React.FC<IOrderConfirmationGroupExtended> = ({
    deliveryProps,
    heading,
    address,
    pickupDateTimeslot,
    salesLines,
    bopis
}) => {
    return (
        <Node {...deliveryProps}>
            {heading}
            {address}
            {bopis && bopis.PHONE && <div>{bopis.PHONE}</div>}
            {pickupDateTimeslot}
        </Node>
    );
};

export const OrderConfirmationGroup: React.FC<IGroupExtended> = ({
    groupProps,
    delivery,
    salesLinesProps,
    salesLines,
    data,
    context,
    resources,
    bopis
}) => {
    const OrderGroup = styled.div`
        display: flex;
        justify-content: flex-start;
        flex-wrap: wrap;
        align-items: center;
        width: 100%;

        @media (max-width: 660px) {
            justify-content: space-evenly;
        }
    `;
    let isVisible: any = [];
    //@ts-ignore
    if (salesLines && salesLines[0] && salesLines[0].address && salesLines[0].address.props) {
        isVisible = salesLines.filter((saleLine: any, index: any) => {
            saleLine.data.product.ExtensionProperties[0].Value.BooleanValue === true ? true : null;
            return (
                saleLine.data.product.ExtensionProperties[0].Value.BooleanValue === true &&
                //@ts-ignore
                salesLines[index].address.props.address.Street !== salesLines[0].address.props.address.Street
            );
            // return saleLine.data.product.ExtensionProperties[0].Value.BooleanValue === true
        });
    }
    let deliveryLabel;
    if (isVisible.length !== 0 && delivery && delivery.shippingItemsToYou) {
        //@ts-ignore
        deliveryLabel = delivery.shippingItemsToYou.props.children.split(' ');
        deliveryLabel.shift();
        deliveryLabel.unshift(resources.deliveringLabel);
        // delivery.shippingItemsToYou.props.children = deliveryLabel.join(' ');
    }

    return (
        <div style={{ width: '100%' }}>
            {delivery && isVisible.length === 0 && <OrderConfirmationGroupDelivery {...delivery} salesLines={salesLines} bopis={bopis} />}
            <div className='ms-order-confirmation__sales-lines-shipping-items-label'>{deliveryLabel && deliveryLabel.join(' ')}</div>
            {delivery && !deliveryLabel && delivery.shippingItemsToYou}
            {salesLines && (
                <OrderGroup>
                    {salesLines.map((salesLine: ISalesLine, index) => {
                        let showAddress = isVisible.length === 0 ? false : true;
                        return (
                            <OrderConfirmationSalesLine
                                key={salesLine.data.salesLine.LineId}
                                context={context}
                                {...salesLine}
                                {...data}
                                resources={resources}
                                quantity={salesLine.data.salesLine.Quantity ? salesLine.data.salesLine.Quantity : undefined}
                                showAddress={showAddress}
                            />
                        );
                    })}
                </OrderGroup>
            )}
        </div>
    );
};

export const OrderConfirmationGroups: React.FC<IGroupsExtended> = props => {
    return (
        <Node {...props.groupsProps}>
            {props.groups.map((group: IGroup, index: number) => (
                <OrderConfirmationGroup key={index} context={props.context} {...group} resources={props.resources} bopis={props.bopis} />
            ))}
        </Node>
    );
};

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => {
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {shipping}
            {tax}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IOrderConfirmationPaymentExtended> = props => {
    const { paymentMethodsProps, title, methods, paymentKey, orderType, resources, salesOrder, bankDeposit } = props;
    if (paymentKey === '7') {
        if (
            salesOrder &&
            salesOrder.ShippingAddress &&
            salesOrder.ShippingAddress.ThreeLetterISORegionName &&
            salesOrder.ShippingAddress.ThreeLetterISORegionName === 'KOR'
        ) {
            return (
                <Node {...paymentMethodsProps}>
                    {title}
                    {resources.orderConfirmationKCP}
                </Node>
            );
        } else {
            return (
                <Node {...paymentMethodsProps}>
                    {title}
                    {methods}
                </Node>
            );
        }
    } else if (paymentKey !== '7' && salesOrder.AttributeValues[0].TextValue) {
        return (
            <Node {...paymentMethodsProps}>
                {title}
                {resources.unitOrderPayment}
            </Node>
        );
    } else if (bankDeposit && bankDeposit.length > 0) {
        return (
            <Node {...paymentMethodsProps}>
                {title}
                {resources.bankPayment}
            </Node>
        );
    } else if (
        paymentKey !== '7' &&
        (orderType === 'Pickup Order' ||
            orderType === 'Curbside pickup' ||
            orderType === 'Pickup' ||
            orderType === 'Curbside' ||
            orderType === 'Curbside Pick up' ||
            orderType === 'Pick Up')
    ) {
        return (
            <Node {...paymentMethodsProps}>
                {title}
                {resources.storePayment}
            </Node>
        );
    } else {
        return (
            <Node {...paymentMethodsProps}>
                {title}
                {methods}
            </Node>
        );
    }
};

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = props => {
    const [checkoutProducts, setCheckoutProducts] = React.useState<any>([]);
    const [adobeCheck, setAdobeCheck] = React.useState<any>(false);
    const [bopisStore, setBopisStore] = React.useState<any>();
    const [channel, setChannel] = React.useState<any>();
    const [warehouse, setWarehouse] = React.useState<any>();
    const backToShoppingUrl = Msdyn365.getUrlSync('home', props.context.actionContext) || '';
    const orderType = props.groups?.groups[0].data.deliveryType;
    const BackToShoppingWrapper = styled.div`
        margin-bottom: 15px;
    `;
    let paymentKey =
        //@ts-ignore
        props.payment && props.payment.methods && props.payment.methods[0] && props.payment.methods[0].key
            ? //@ts-ignore
              props.payment.methods[0].key
            : '7';

    let bankDepositPrimary;
    let bankDepositSecondary;
    if (props.salesOrder && props.salesOrder.AttributeValues) {
        bankDepositPrimary = props.salesOrder.AttributeValues.filter((attribute: any) => {
            return attribute.Name === 'BankDepositPrimaryMessage';
        });
        bankDepositSecondary = props.salesOrder.AttributeValues.filter((attribute: any) => {
            return attribute.Name === 'BankDepositSecondaryMessage';
        });
    }
    const getSku = async (Product: any) => {
        const ctx = { callerContext: props.context.actionContext };
        const { ProductId, Price, ItemId } = Product.salesLine;
        const { Name, Dimensions } = Product.product;

        const productAttributes = await getAttributeValuesAsync(ctx, ProductId, props.context.request.channel!.RecordId, 0).catch(
            (err: any) => {
                console.log(err);
            }
        );
        const language = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 4;
        });
        const Style = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 1;
        });
        const Size = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 3;
        });
        let fulfillableSku;
        if (productAttributes) {
            fulfillableSku = productAttributes.filter((item: any) => {
                return item.RecordId === 5637144592;
            });
        }

        const adobeItem = {
            info: {
                sku: fulfillableSku ? fulfillableSku[0].TextValue : '',
                id: ItemId,
                name: Name,
                language: language && language[0] && language[0].DimensionValue ? language[0].DimensionValue.Value : '',
                style: Style && Style[0] && Style[0].DimensionValue ? Style[0].DimensionValue.Value : '',
                size: Size && Size[0] && Size[0].DimensionValue ? Size[0].DimensionValue.Value : ''
            },
            quantity: Product.salesLine.Quantity,
            price: {
                basePrice: Price
            }
        };

        setCheckoutProducts((prevState: any) => [...prevState, adobeItem]);
    };

    let paymentTypeName: any;

    if (paymentKey === '7' || (props.salesOrder && props.salesOrder.TenderLines && props.salesOrder.TenderLines[0].CardTypeId !== '')) {
        paymentTypeName = props.salesOrder && props.salesOrder.TenderLines ? 'Credit/Debit' : '';
    } else if (
        paymentKey !== '7' &&
        props.salesOrder?.AttributeValues &&
        // @ts-ignore
        props.salesOrder.AttributeValues[0].TextValue
    ) {
        //@ts-ignore
        paymentTypeName = 'Unit Order';
    } else if (bankDepositPrimary && bankDepositSecondary && bankDepositPrimary.length > 0) {
        paymentTypeName = 'Bank Deposit';
    } else if (
        paymentKey !== '7' &&
        //@ts-ignore
        (orderType === 'Pickup Order' ||
            //@ts-ignore
            orderType === 'Curbside pickup' ||
            //@ts-ignore
            orderType === 'Pickup' ||
            //@ts-ignore
            orderType === 'Curbside' ||
            //@ts-ignore
            orderType === 'Curbside Pick up' ||
            //@ts-ignore
            orderType === 'Pick Up')
    ) {
        //@ts-ignore
        paymentTypeName = props.resources.storePayment;
    }

    const AdobeAnalyticsCheckout = async () => {
        let orderId = window.sessionStorage.getItem('orderId');
        if (orderId !== props.salesOrder?.ChannelReferenceId && adobeCheck !== true) {
            if (window && window['digitalData']) {
                window['digitalData'].push({
                    event: 'Purchase',
                    transaction: {
                        id: props.salesOrder ? props.salesOrder.ChannelReferenceId : '',
                        item: checkoutProducts,
                        total: {
                            transactionTotal: props.salesOrder ? props.salesOrder.GrossAmount : '',
                            currency: props.salesOrder ? props.salesOrder.CurrencyCode : ''
                        },
                        attributes: {
                            paymentMethod: paymentTypeName
                        }
                    }
                });
            }
        }
    };

    React.useEffect(() => {
        const orderHistory = sessionStorage.getItem('orderHistory');
        if (props.salesLines && checkoutProducts.length === 0) {
            props.salesLines.map((item: any) => {
                void getSku(item.data);
            });
        }
        if (props.salesLines && checkoutProducts.length === props.salesLines.length && orderHistory !== 'true') {
            AdobeAnalyticsCheckout();
            setAdobeCheck(true);
        }
        if (props.salesOrder && adobeCheck === true) {
            window.sessionStorage.setItem('orderId', props.salesOrder.ChannelReferenceId ? props.salesOrder.ChannelReferenceId : '');
        }
    }, [checkoutProducts, adobeCheck]);
    let pickupStore: any;

    const getPickupStore = async () => {
        const ctx = { callerContext: props.context.actionContext };
        let stores = await getStoreHoursAsync(ctx, 'ZAF');
        if (stores) {
            pickupStore = stores.filter((store: any) => {
                return (
                    store.ZIPCODE === props.salesOrder?.ShippingAddress?.ZipCode &&
                    store.STREET === props.salesOrder?.ShippingAddress?.Street
                );
            });
        }
        setBopisStore(pickupStore[0]);

        return stores;
    };
    const getRetailChannel = async () => {
        const ctx = { callerContext: props.context.actionContext };

        // @ts-ignore
        const channel = await getRetailChannelAsync(ctx, props.context.request.channel?.RecordId);
        let warehouseId = props.salesOrder && props.salesOrder.InventoryLocationId ? props.salesOrder.InventoryLocationId : '';

        const warehouse = await getWarehouseAsync(ctx, warehouseId);

        setChannel(channel);
        setWarehouse(warehouse);
    };

    React.useEffect(() => {
        sessionStorage.setItem('taxIdCheck', 'false');
        sessionStorage.setItem('idNum', '');
        sessionStorage.setItem('TaxId', '');
        sessionStorage.setItem('taxIdRegion', '');
        sessionStorage.setItem('emailOnCart', 'false');
        sessionStorage.setItem('paymentTypeId', '');
        sessionStorage.setItem('email', '');
        sessionStorage.setItem('phone', '');

        void getPickupStore();
        void getRetailChannel();
    }, []);

    return (
        <Module {...props.moduleProps}>
            {props.heading}
            {props.alert}
            {props.loading}
            {warehouse && channel && props.orderInfomation && (
                <div style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-around' }}>
                    {channel && channel.NotificationCity ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h3>{channel.NotificationDescription || ''}</h3>
                            <div>{channel.NotificationStreet || ''}</div>
                            <div>
                                {channel.NotificationCity || ''}, {channel.NotificationState || ''} {channel.NotificationZipCode || ''}
                            </div>
                            <div>{channel.NotificationRegionId || ''}</div>
                        </div>
                    ) : null}
                    {warehouse && warehouse.NotificationCity ? (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h3>{warehouse.NotificationDescription || ''}</h3>
                            <div>{warehouse.NotificationStreet || ''}</div>
                            <div>
                                {warehouse.NotificationCity || ''}, {warehouse.NotificationState || ''}{' '}
                                {warehouse.NotificationZipCode || ''}
                            </div>
                            <div>{warehouse.NotificationRegionId || ''}</div>
                        </div>
                    ) : null}
                </div>
            )}
            {/* @ts-ignore */}
            {bankDepositSecondary && bankDepositSecondary[0] ? bankDepositSecondary[0].TextValue : ''}
            {bankDepositPrimary && bankDepositPrimary[0] ? (
                // @ts-ignore
                <div dangerouslySetInnerHTML={{ __html: bankDepositPrimary[0].TextValue }} />
            ) : (
                ''
            )}
            <br />
            {props.orderInfomation && (
                <OrderConfirmationOrderInfomation {...props.orderInfomation} orderType={orderType} resources={props.resources} />
            )}
            <BackToShoppingWrapper>
                <Primary href={backToShoppingUrl}>{props.resources.backToShopping}</Primary>
            </BackToShoppingWrapper>
            {props.groups && (
                <OrderConfirmationGroups context={props.context} {...props.groups} resources={props.resources} bopis={bopisStore} />
            )}
            {props.payment && (
                <OrderConfirmationPayment
                    {...props.payment}
                    paymentKey={paymentKey}
                    orderType={orderType}
                    resources={props.resources}
                    salesOrder={props.salesOrder}
                    bankDeposit={bankDepositPrimary}
                />
            )}
            {props.orderSummary && <OrderConfirmationOrderSummary {...props.orderSummary} />}
            {props.help && <OrderConfirmationHelp {...props.help} />}
        </Module>
    );
};

export default OrderConfirmationView;
